import { FormEvent, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import api, { Lane, VinBatch } from '../lib/api'

export default function VinInventoryImportPage() {
  const [rawText, setRawText] = useState('')
  const [customerId, setCustomerId] = useState('')
  const [laneId, setLaneId] = useState('')
  const [notes, setNotes] = useState('')
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([])
  const [lanes, setLanes] = useState<Lane[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{
    batch: VinBatch
    summary: { rawCount: number; validCount: number; duplicateCount: number; invalidCount: number; decodedCount: number; failedDecodeCount: number }
  } | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        const [customerRes, laneRes] = await Promise.all([
          api.customers.list(),
          api.lanes.list({ status: 'ACTIVE' }),
        ])
        setCustomers(customerRes.data.map((item) => ({ id: item.id, name: item.name })))
        setLanes(laneRes.data)
      } catch {
        // Lookup loading is best-effort.
      }
    }
    void load()
  }, [])

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setResult(null)
    if (!rawText.trim()) {
      setError('Paste VIN text before submitting.')
      return
    }

    setSaving(true)
    try {
      const response = await api.vinBatches.create({
        rawText,
        customerId: customerId || undefined,
        laneId: laneId || undefined,
        notes: notes || undefined,
        sourceType: 'paste',
      })
      setResult({ batch: response.data.batch, summary: response.data.summary })
      setRawText('')
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to import VIN batch')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">VIN Inventory Import</h1>
        <p className="text-sm text-gray-500 mt-1">Paste VINs in bulk, decode/store them, and create a reviewable VIN batch.</p>
      </div>

      {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      {result && (
        <div className="rounded border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 space-y-2">
          <div className="font-semibold">Batch {result.batch.batchNumber} created.</div>
          <div>Raw: {result.summary.rawCount} | Valid: {result.summary.validCount} | Duplicates: {result.summary.duplicateCount} | Invalid: {result.summary.invalidCount}</div>
          <div>Decoded: {result.summary.decodedCount} | Failed decode: {result.summary.failedDecodeCount}</div>
          <Link to={`/vin-batches/${result.batch.id}`} className="text-emerald-800 underline">Open batch detail</Link>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4 bg-white border rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <select className="border rounded px-3 py-2 text-sm" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            <option value="">Customer (optional)</option>
            {customers.map((customer) => (
              <option key={customer.id} value={customer.id}>{customer.name}</option>
            ))}
          </select>

          <select className="border rounded px-3 py-2 text-sm" value={laneId} onChange={(e) => setLaneId(e.target.value)}>
            <option value="">Lane (optional)</option>
            {lanes.map((lane) => (
              <option key={lane.id} value={lane.id}>{lane.laneNumber} | {lane.originZip || '-'} to {lane.destinationZip || '-'}</option>
            ))}
          </select>

          <textarea
            className="md:col-span-2 border rounded px-3 py-2 text-sm min-h-28"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Import notes (optional)"
          />
        </div>

        <textarea
          className="w-full border rounded px-3 py-2 text-sm min-h-72 font-mono"
          value={rawText}
          onChange={(e) => setRawText(e.target.value)}
          placeholder="Paste VINs here. Supports one VIN per line, comma/space/tab separated values, and VINs embedded in copied text."
        />

        <div className="flex items-center gap-2">
          <button disabled={saving} className="bg-blue-700 text-white px-4 py-2 rounded text-sm">
            {saving ? 'Importing...' : 'Create VIN Batch'}
          </button>
          <Link to="/vin-batches" className="border rounded px-4 py-2 text-sm">View Batches</Link>
          <Link to="/vin-inventory" className="border rounded px-4 py-2 text-sm">View Inventory</Link>
        </div>
      </form>
    </div>
  )
}
